import { XMarkIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator } from '../../../../components'
import { LockPill } from '../../../../components/pricing/lock-badge'
import SampleBadge from '../../../../components/sample-data/sample-badge'
import { Heading } from '../../../../components/ui/Heading'
import { useUserContext } from '../../../../context'
import { isSampleSpace } from '../../../../helpers/sample-data'
import { useTalentQuery } from '../../../../hooks/useTalentQuery'
import { useTalentStorage } from '../../../pricing/hooks'
import TalentBadge from '../TalentBadge'

const truncateName = (name, maxLength = 15) => {
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name
}

export default function SelectionTalentCard({ talentOwnerId, onClickClose }) {
  const { t } = useTranslation()

  const {
    cognitoUser: { username }
  } = useUserContext()

  const { data: talent, isLoading } = useTalentQuery(talentOwnerId, username)

  // Gesperrte Talente bleiben mit Namen sichtbar und lassen sich abwählen.
  // Solange nicht gezählt ist oder das Talent noch nachlädt, steht noch kein
  // Schloss da. Gesperrt wird je Person, also zählt auch der owner.
  const { pending, isLocked, isPendingId, isOwnerLocked, isPendingOwner } =
    useTalentStorage({ fetch: 'auto' })
  const owner = talent?.owner
  const locked =
    !pending &&
    !isPendingId?.(talentOwnerId) &&
    (isLocked(talentOwnerId) ||
      (!!owner && !isPendingOwner?.(owner) && !!isOwnerLocked?.(owner)))

  if (isLoading) {
    return (
      <div className='flex h-[46px] w-64 flex-row items-center justify-center rounded-lg border border-softGrey bg-white px-4 py-2'>
        <ActivityIndicator className='flex justify-center' />
      </div>
    )
  }

  const hasTalentName =
    (talent?.person?.firstname && talent?.person?.lastname) ||
    talent?.person?.invitation_firstname ||
    talent?.person?.invitation_lastname

  const displayName = hasTalentName
    ? `${talent?.person?.firstname} ${talent?.person?.lastname}`
    : t('common.unknown_talent')

  return (
    <div
      className={clsx(
        'flex flex-row items-center justify-between rounded-lg border border-softGrey bg-white px-4 py-2',
        // Mit Schloss-Pille passt die Karte nicht in w-64
        locked ? 'min-w-64 gap-x-3' : 'w-64'
      )}
    >
      <div className='flex flex-row items-center gap-2'>
        <TalentBadge talentName={displayName} />
        <Heading title={displayName} level={4}>
          {truncateName(displayName)}
        </Heading>
      </div>
      <div className='flex flex-row items-center gap-1'>
        {locked && (
          <span title={t('pricing.talent_locked.row_sr')}>
            <LockPill label={t('pricing.talent_locked.placeholder')} />
          </span>
        )}
        {isSampleSpace(talent) && <SampleBadge kind='talent' />}
        <XMarkIcon
          className='size-4 cursor-pointer text-darkGrey'
          onClick={onClickClose}
        />
      </div>
    </div>
  )
}

SelectionTalentCard.propTypes = {
  // Space-ID aus ?selection=, der Name ist historisch
  talentOwnerId: PropTypes.string.isRequired,
  onClickClose: PropTypes.func.isRequired
}
